<script setup>
import { computed } from 'vue';
import ButtonV4 from 'next/button/Button.vue';

const props = defineProps({
  availableAddons: {
    type: Array,
    default: () => [],
  },
  accountAddons: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['addAddon', 'removeAddon']);

const addonTypeIcons = {
  resource: 'i-lucide-package',
  feature: 'i-lucide-sparkles',
  credit: 'i-lucide-coins',
  storage: 'i-lucide-hard-drive',
  integration: 'i-lucide-plug',
};

const addonTypeColors = {
  resource: 'text-blue-500',
  feature: 'text-amber-500',
  credit: 'text-green-500',
  storage: 'text-slate-500',
  integration: 'text-pink-500',
};

const getAddonIcon = type => addonTypeIcons[type] || 'i-lucide-box';
const getAddonColor = type => addonTypeColors[type] || 'text-slate-500';

// Get active account addons with their details
const activeAddons = computed(() => {
  return props.accountAddons
    .filter(a => a.status === 'active' && a.addon)
    .map(accountAddon => ({
      ...accountAddon,
      addonDetails: accountAddon.addon,
      totalUnits:
        (accountAddon.quantity || 1) * (accountAddon.addon?.unit_quantity || 1),
    }));
});

// Check if addon has active purchases
const getActiveAddonInfo = addonId => {
  const active = props.accountAddons.filter(
    a => a.addon?.id === addonId && a.status === 'active'
  );
  if (active.length === 0) return null;

  // Sum all quantities for this addon
  const totalQuantity = active.reduce((sum, a) => sum + (a.quantity || 1), 0);
  const unitQty = active[0]?.addon?.unit_quantity || 1;

  return {
    accountAddonId: active[0].id,
    quantity: totalQuantity,
    totalUnits: totalQuantity * unitQty,
  };
};

const formatPrice = price => {
  if (!price) return 'Grátis';
  return `R$ ${parseFloat(price).toFixed(2).replace('.', ',')}`;
};

const formatUnits = (addon, quantity = 1) => {
  const units = (addon.unit_quantity || 1) * quantity;
  if (addon.addon_type === 'credit') {
    return `${units} créditos`;
  }
  if (addon.addon_type === 'storage') {
    return `${units} MB`;
  }
  return `${units} unidade(s)`;
};

const handleAddAddon = addon => {
  emit('addAddon', addon);
};

const handleRemoveAddon = accountAddonId => {
  emit('removeAddon', accountAddonId);
};
</script>

<template>
  <div class="space-y-6">
    <!-- Active Add-ons Section -->
    <div v-if="activeAddons.length > 0">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ $t('BILLING_SETTINGS.ADDONS.ACTIVE_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10">
            {{ $t('BILLING_SETTINGS.ADDONS.ACTIVE_DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="accountAddon in activeAddons"
          :key="accountAddon.id"
          class="relative p-4 rounded-xl border border-n-teal-9 bg-n-teal-2 transition-all"
        >
          <!-- Active badge -->
          <div
            class="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-n-teal-9 text-white text-xs font-medium"
          >
            {{ $t('BILLING_SETTINGS.ADDONS.ACTIVE') }}
          </div>

          <!-- Icon and title -->
          <div class="flex items-start gap-3 mb-3">
            <div
              class="w-10 h-10 rounded-lg flex items-center justify-center bg-n-slate-3"
            >
              <span
                class="text-lg"
                :class="[
                  getAddonIcon(accountAddon.addonDetails?.addon_type),
                  getAddonColor(accountAddon.addonDetails?.addon_type),
                ]"
              />
            </div>
            <div class="flex-1 min-w-0 pr-16">
              <h4 class="text-sm font-semibold text-n-slate-12 truncate">
                {{ accountAddon.addonDetails?.name }}
              </h4>
              <p class="text-xs text-n-slate-10 line-clamp-2">
                {{ accountAddon.addonDetails?.description }}
              </p>
            </div>
          </div>

          <!-- Price -->
          <div class="flex items-baseline gap-1 mb-2">
            <span class="text-lg font-bold text-woot-600">
              {{ formatPrice(accountAddon.addonDetails?.monthly_price) }}
            </span>
            <span class="text-xs text-n-slate-10">
              /{{ $t('BILLING_SETTINGS.ADDONS.MONTH') }}
            </span>
          </div>

          <!-- Quantity info -->
          <div class="text-xs text-n-slate-11 mb-3 font-medium">
            {{ formatUnits(accountAddon.addonDetails, accountAddon.quantity) }}
          </div>

          <!-- Remove button -->
          <div class="mt-3">
            <ButtonV4
              xs
              faded
              ruby
              class="w-full"
              :is-loading="isLoading"
              @click="handleRemoveAddon(accountAddon.id)"
            >
              {{ $t('BILLING_SETTINGS.ADDONS.REMOVE') }}
            </ButtonV4>
          </div>
        </div>
      </div>
    </div>

    <!-- Available Add-ons Section -->
    <div>
      <div class="flex items-center justify-between mb-4">
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ $t('BILLING_SETTINGS.ADDONS.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-10">
            {{ $t('BILLING_SETTINGS.ADDONS.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div v-if="availableAddons.length === 0" class="text-center py-8">
        <span class="i-lucide-package-x text-3xl text-n-slate-8 mb-2" />
        <p class="text-sm text-n-slate-10">
          {{ $t('BILLING_SETTINGS.ADDONS.NO_ADDONS') }}
        </p>
      </div>

      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div
          v-for="addon in availableAddons"
          :key="addon.id"
          class="relative p-4 rounded-xl border border-n-weak bg-n-solid-2 hover:border-n-slate-7 transition-all"
        >
          <!-- Already purchased indicator -->
          <div
            v-if="getActiveAddonInfo(addon.id)"
            class="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-n-blue-9 text-white text-xs font-medium"
          >
            {{
              $t('BILLING_SETTINGS.ADDONS.PURCHASED_COUNT', {
                count: getActiveAddonInfo(addon.id).quantity,
              })
            }}
          </div>

          <!-- Icon and title -->
          <div class="flex items-start gap-3 mb-3">
            <div
              class="w-10 h-10 rounded-lg flex items-center justify-center bg-n-slate-3"
            >
              <span
                class="text-lg"
                :class="[
                  getAddonIcon(addon.addon_type),
                  getAddonColor(addon.addon_type),
                ]"
              />
            </div>
            <div
              class="flex-1 min-w-0"
              :class="getActiveAddonInfo(addon.id) ? 'pr-20' : ''"
            >
              <h4 class="text-sm font-semibold text-n-slate-12 truncate">
                {{ addon.name }}
              </h4>
              <p class="text-xs text-n-slate-10 line-clamp-2">
                {{ addon.description }}
              </p>
            </div>
          </div>

          <!-- Price -->
          <div class="flex items-baseline gap-1 mb-2">
            <span class="text-lg font-bold text-woot-600">
              {{ formatPrice(addon.monthly_price) }}
            </span>
            <span class="text-xs text-n-slate-10">
              /{{ $t('BILLING_SETTINGS.ADDONS.MONTH') }}
            </span>
          </div>

          <!-- Unit quantity info -->
          <div class="text-xs text-n-slate-10 mb-3">
            {{ formatUnits(addon) }}
          </div>

          <!-- Add button (always available) -->
          <div class="mt-3">
            <ButtonV4
              xs
              solid
              blue
              class="w-full"
              :is-loading="isLoading"
              @click="handleAddAddon(addon)"
            >
              <span class="i-lucide-plus mr-1" />
              {{
                getActiveAddonInfo(addon.id)
                  ? $t('BILLING_SETTINGS.ADDONS.BUY_MORE')
                  : $t('BILLING_SETTINGS.ADDONS.ADD')
              }}
            </ButtonV4>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
